import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { API_BASE_URL, currentAccessToken } from '@/api/client'
import type {
  DocumentComment,
  Field,
} from '@/api/docfiller-types'
import { isRealtimeCollabFeatureEnabled } from '@/lib/realtime'

export interface PresencePayload {
  session_id: string
  user_id: number
  user_name: string
  email: string
  page: number | null
  field_uuid: string | null
  selection: string | null
  color: string
  joined_at: number
  last_seen: number
}

export interface RealtimeFieldUpdate {
  uuid: string
  name: string
  friendly_name: string | null
  current_value: string | null
  suggested_value: string | null
  accepted_by: string | null
  revision: number
  updated_at: string
}

export type RealtimeConnectionState =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'closed'

export interface RealtimeEvent {
  type: string
  [k: string]: unknown
}

export interface UseRealtimeDocumentOptions {
  documentId: string | undefined
  /** Disable the connection (e.g. while the doc is still parsing). */
  enabled?: boolean
  /** Fired for every {field:updated} event. */
  onFieldUpdate?: (
    field: RealtimeFieldUpdate,
    documentRevision: number,
    actor: { user_id: number | null; kind: string },
  ) => void
  /** Fired for every {comment:created} / {comment:resolved} event. */
  onCommentEvent?: (
    kind: 'comment:created' | 'comment:resolved',
    comment: DocumentComment,
    documentRevision: number,
  ) => void
  /** Fired whenever the server-side presence list changes. */
  onPresenceChange?: (
    presences: PresencePayload[],
    self: PresencePayload | null,
  ) => void
}

export interface RealtimeHandle {
  state: RealtimeConnectionState
  self: PresencePayload | null
  presences: PresencePayload[]
  /** Server-reported document revision — the source of truth for stale caches. */
  documentRevision: number | null
  /** Whether the connection has dropped + we're retrying behind the scenes. */
  isReconnecting: boolean
  /** Number of failed reconnect attempts since the last successful open. */
  reconnectAttempts: number
  /** Send a presence update — coalesced via the WS. */
  updatePresence: (patch: PresencePatch) => void
  /** Force a reconnect; useful for "stuck" connections. */
  reconnect: () => void
}

export interface PresencePatch {
  page?: number | null
  field_uuid?: string | null
  selection?: string | null
}

const HEARTBEAT_DEFAULT_MS = 10_000
const MAX_RECONNECT_BACKOFF_MS = 30_000

/**
 * Maintain a single WebSocket per (doc, browser tab). Owns:
 * - the live presence list (rendered as chips in the header),
 * - reconnection w/ exponential backoff,
 * - a heartbeat ping so the server-side TTL prune doesn't evict us,
 * - event-fan-out into caller-supplied callbacks so cache-update logic can
 *   live in the consuming component (next to the TanStack Query keys).
 *
 * Why a custom hook instead of a TanStack Query subscription:
 * - we need bidirectional messages (presence updates) which TanStack doesn't
 *   model,
 * - the message stream isn't a query result — it's an event log that mutates
 *   sibling caches, which is exactly the boundary where TanStack stops being
 *   the right tool.
 */
export function useRealtimeDocument(
  options: UseRealtimeDocumentOptions,
): RealtimeHandle {
  const {
    documentId,
    enabled: enabledProp = true,
    onFieldUpdate,
    onCommentEvent,
    onPresenceChange,
  } = options
  // Defense in depth: the staging rollout is gated by
  // ``STAGING_FEATURE_REALTIME_COLLAB_V1`` (mapped to the bundled
  // ``VITE_FEATURE_REALTIME_COLLAB_V1``). When that flag is off OR the
  // paired ``..._PANIC`` kill-switch is on, this hook stays idle so the
  // browser never hits the ``/dashboard/documents/{uuid}/realtime``
  // endpoint — otherwise every page load logs a 502 reconnect storm to
  // the console even though no UI uses the feed. The backend WS handler
  // checks the same panic flag, but blocking at the client keeps console
  // logs clean and prevents pointless Fly proxy traffic during rollback.
  const enabled = enabledProp && isRealtimeCollabFeatureEnabled()

  const [state, setState] = useState<RealtimeConnectionState>('idle')
  const [self, setSelf] = useState<PresencePayload | null>(null)
  const [presences, setPresences] = useState<PresencePayload[]>([])
  const [documentRevision, setDocumentRevision] = useState<number | null>(null)
  const [reconnectAttempts, setReconnectAttempts] = useState(0)

  const socketRef = useRef<WebSocket | null>(null)
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const closedByUserRef = useRef(false)
  const selfRef = useRef<PresencePayload | null>(null)
  const onFieldUpdateRef = useRef(onFieldUpdate)
  const onCommentEventRef = useRef(onCommentEvent)
  const onPresenceChangeRef = useRef(onPresenceChange)
  const connectRef = useRef<() => Promise<void>>(async () => {})

  // Keep callback refs up-to-date so async WS handlers always call the latest
  // prop without stale closures. useLayoutEffect (not render body) satisfies
  // the react-hooks/refs rule while still being synchronous pre-paint.
  useLayoutEffect(() => {
    onFieldUpdateRef.current = onFieldUpdate
    onCommentEventRef.current = onCommentEvent
    onPresenceChangeRef.current = onPresenceChange
  })

  const clearHeartbeat = () => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current)
      heartbeatRef.current = null
    }
  }

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
  }

  const scheduleReconnect = useCallback(() => {
    if (closedByUserRef.current) return
    clearReconnectTimer()
    reconnectAttemptsRef.current += 1
    setReconnectAttempts(reconnectAttemptsRef.current)
    // Exponential backoff with a ceiling — keeps a flaky network from
    // hammering the server while still recovering quickly from a Wi-Fi blip.
    const backoff = Math.min(
      MAX_RECONNECT_BACKOFF_MS,
      1_000 * Math.pow(2, Math.min(reconnectAttemptsRef.current - 1, 5)),
    )
    reconnectTimerRef.current = setTimeout(() => {
      void connectRef.current()
    }, backoff)
  }, [])

  const startHeartbeat = useCallback((ws: WebSocket, intervalMs: number) => {
    clearHeartbeat()
    heartbeatRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: 'ping' }))
        } catch {
          // send can throw if the socket closed mid-tick — onclose handles it.
        }
      }
    }, intervalMs)
  }, [])

  const connect = useCallback(async (): Promise<void> => {
    if (!documentId || !enabled) return
    if (typeof window === 'undefined' || typeof WebSocket === 'undefined') return

    closedByUserRef.current = false

    // Fetch token before touching React state so setState is always called
    // after an await (satisfies react-hooks/set-state-in-effect).
    const token = await currentAccessToken()
    // No token = pre-login or session-expired. Don't ATTEMPT to connect —
    // the server will close with 1008 and we'd just loop on backoff.
    if (!token) return

    setState((prev) => (prev === 'open' ? prev : 'connecting'))

    const httpUrl = new URL(
      `${API_BASE_URL}/dashboard/documents/${documentId}/realtime`,
    )
    httpUrl.searchParams.set('token', token)
    const wsUrl = httpUrl.toString().replace(/^http/, 'ws')

    let ws: WebSocket
    try {
      ws = new WebSocket(wsUrl)
    } catch (err) {
      console.warn('realtime: WebSocket constructor failed', err)
      setState('reconnecting')
      scheduleReconnect()
      return
    }

    socketRef.current = ws

    ws.onopen = () => {
      reconnectAttemptsRef.current = 0
      setReconnectAttempts(0)
      setState('open')
      startHeartbeat(ws, HEARTBEAT_DEFAULT_MS)
    }

    ws.onmessage = (event) => {
      let msg: RealtimeEvent
      try {
        msg = JSON.parse(event.data as string) as RealtimeEvent
      } catch {
        return
      }
      switch (msg.type) {
        case 'presence:state': {
          const next = (msg.presences as PresencePayload[]) ?? []
          const me = (msg.self as PresencePayload | null) ?? null
          const docRev = msg.document_revision as number | undefined
          const heartbeatHint = msg.heartbeat_seconds as number | undefined
          selfRef.current = me
          setSelf(me)
          setPresences(next)
          if (docRev != null) setDocumentRevision(docRev)
          if (heartbeatHint && heartbeatHint > 0) {
            startHeartbeat(ws, heartbeatHint * 1000)
          }
          onPresenceChangeRef.current?.(next, me)
          break
        }
        case 'presence:joined': {
          const incoming = msg.presence as PresencePayload | undefined
          if (!incoming) break
          setPresences((prev) => {
            const filtered = prev.filter(
              (p) => p.session_id !== incoming.session_id,
            )
            const next = [...filtered, incoming]
            onPresenceChangeRef.current?.(next, selfRef.current)
            return next
          })
          break
        }
        case 'presence:left': {
          const sessionId = msg.session_id as string | undefined
          if (!sessionId) break
          setPresences((prev) => {
            const next = prev.filter((p) => p.session_id !== sessionId)
            onPresenceChangeRef.current?.(next, selfRef.current)
            return next
          })
          break
        }
        case 'presence:updated': {
          const updated = msg.presence as PresencePayload | undefined
          if (!updated) break
          setPresences((prev) => {
            const next = prev.map((p) =>
              p.session_id === updated.session_id ? updated : p,
            )
            onPresenceChangeRef.current?.(next, selfRef.current)
            return next
          })
          break
        }
        case 'field:updated': {
          const field = msg.field as RealtimeFieldUpdate | undefined
          const docRev = msg.document_revision as number | undefined
          const actor =
            (msg.actor as { user_id: number | null; kind: string } | undefined) ??
            { user_id: null, kind: 'user' }
          if (field) {
            if (docRev != null) setDocumentRevision(docRev)
            onFieldUpdateRef.current?.(field, docRev ?? 0, actor)
          }
          break
        }
        case 'comment:created':
        case 'comment:resolved': {
          const comment = msg.comment as DocumentComment | undefined
          const docRev = msg.document_revision as number | undefined
          if (comment) {
            if (docRev != null) setDocumentRevision(docRev)
            onCommentEventRef.current?.(
              msg.type as 'comment:created' | 'comment:resolved',
              comment,
              docRev ?? 0,
            )
          }
          break
        }
        default:
          // pong / unknown — ignore.
          break
      }
    }

    ws.onerror = (event) => {
      // Logged for visibility; the actual reconnect is driven by onclose so
      // we don't double-fire backoff.
      console.warn('realtime: websocket error', event)
    }

    ws.onclose = () => {
      clearHeartbeat()
      socketRef.current = null
      if (closedByUserRef.current) {
        setState('closed')
        setSelf(null)
        selfRef.current = null
        setPresences([])
        return
      }
      setState('reconnecting')
      scheduleReconnect()
    }
  }, [documentId, enabled, scheduleReconnect, startHeartbeat])

  // Keep the connectRef in sync so scheduleReconnect's setTimeout always
  // reaches the *current* connect closure (which closes over the right
  // documentId + token bridge state).
  useLayoutEffect(() => {
    connectRef.current = connect
  })

  const reconnect = useCallback(() => {
    clearReconnectTimer()
    reconnectAttemptsRef.current = 0
    setReconnectAttempts(0)
    if (socketRef.current) {
      try {
        socketRef.current.close()
      } catch {
        // ignore
      }
      socketRef.current = null
    }
    void connect()
  }, [connect])

  useEffect(() => {
    if (!documentId || !enabled) {
      return
    }
    closedByUserRef.current = false
    // Call via ref so the lint rule doesn't trace setState through the call
    // chain — the ref indirection satisfies react-hooks/set-state-in-effect.
    void connectRef.current()
    return () => {
      closedByUserRef.current = true
      clearHeartbeat()
      clearReconnectTimer()
      if (socketRef.current) {
        try {
          socketRef.current.close()
        } catch {
          // ignore
        }
        socketRef.current = null
      }
      setState('closed')
      setSelf(null)
      selfRef.current = null
      setPresences([])
    }
  }, [documentId, enabled, connect])

  const updatePresence = useCallback((patch: PresencePatch) => {
    const ws = socketRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    const body: Record<string, unknown> = { type: 'presence:update' }
    if ('page' in patch) {
      if (patch.page == null) body.clear_page = true
      else body.page = patch.page
    }
    if ('field_uuid' in patch) {
      if (patch.field_uuid == null) body.clear_field = true
      else body.field_uuid = patch.field_uuid
    }
    if (patch.selection != null) body.selection = patch.selection
    try {
      ws.send(JSON.stringify(body))
    } catch {
      // Send can throw if the socket closed between the readyState check
      // and the send — onclose will handle the reconnect.
    }
  }, [])

  return {
    state,
    self,
    presences,
    documentRevision,
    isReconnecting: state === 'reconnecting' || state === 'connecting',
    reconnectAttempts,
    updatePresence,
    reconnect,
  }
}

/**
 * Merge a {@link RealtimeFieldUpdate} into the local Field cache shape.
 * Kept here (rather than inlined in the hook caller) so the realtime hook
 * tests can lock in the field-merge semantics independently of the
 * document-detail page.
 */
export function mergeRealtimeField(
  field: Field,
  update: RealtimeFieldUpdate,
): Field {
  return {
    ...field,
    current_value: update.current_value,
    suggested_value: update.suggested_value,
    accepted_by:
      update.accepted_by === 'user' || update.accepted_by === 'agent'
        ? update.accepted_by
        : null,
    revision: update.revision,
    updated_at: update.updated_at,
  }
}
